/**
 * The roster card's header: title, count, search and sort (BON-538).
 *
 * Its own component because the phone arrangement is a different shape, not a
 * narrower one — the title takes a line of its own and the search runs full
 * width beneath it, so the row would otherwise be two layouts interleaved by
 * conditionals inside one function.
 */
import { Checkbox } from '../../primitives/Checkbox';
import { CountBadge } from '../../primitives/CountBadge';
import { SearchInput } from '../../primitives/SearchInput';
import { SortSelect } from '../../primitives/SortSelect';
import type { TableSort } from '../../primitives/useTableSort';

import type { VenueRosterRow } from './venues-roster-api';

export interface VenueRosterHeaderProps {
  count: number;
  search: string;
  onSearchChange: (value: string) => void;
  sort: TableSort<VenueRosterRow>;
  isPhone: boolean;
  /** Only venues with open action items. */
  tasksOnly: boolean;
  onTasksOnlyChange: (value: boolean) => void;
}

export function VenueRosterHeader({
  count,
  search,
  onSearchChange,
  sort,
  isPhone,
  tasksOnly,
  onTasksOnlyChange,
}: VenueRosterHeaderProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: isPhone ? 10 : 14,
        padding: isPhone ? '14px 16px' : '14px 18px',
        // The phone stack's cards carry their own borders, so a rule under the
        // header would be a second line above the first card.
        borderBottom: isPhone ? 'none' : '1px solid var(--nb-border)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          flex: isPhone ? '1 0 100%' : '0 0 auto',
        }}
      >
        <h2 className="nb-display nb-eyebrow" style={{ margin: 0, fontSize: 12 }}>
          Venue roster
        </h2>
        <CountBadge count={count} />
      </div>
      {!isPhone && <div style={{ flex: 1 }} />}
      <SearchInput
        value={search}
        onChange={onSearchChange}
        placeholder="Search venues…"
        ariaLabel="Search venues by name"
        style={isPhone ? { flex: '1 0 100%' } : { flex: '0 1 260px' }}
      />
      {/* A touch target of 44px on a phone, on a row of its own so it does not crowd the sort. */}
      <div style={isPhone ? { flex: '1 0 100%' } : undefined}>
        <Checkbox
          checked={tasksOnly}
          onChange={onTasksOnlyChange}
          label="Only venues with open tasks"
          minHeight={isPhone ? 44 : undefined}
        />
      </div>
      <SortSelect
        value={sort.sortId}
        onChange={sort.setSortId}
        options={sort.options}
        ariaLabel="Sort venues"
      />
    </div>
  );
}
